import React from "react";
import {
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import ImageUploadField from "../ImageUploadField/ImageUploadField";
import ColumnEditor from "../ColumnEditor/ColumnEditor";
import RichTextEditor from "../../../../../common/RichTextEditor";

/**
 * Renders a single field from a section-type registry field descriptor.
 * Supported types: text | textarea | url | number | image | file | boolean |
 * select | richtext | code | column.
 */
const SalesFieldRenderer = ({ field, value, page, disabled, onChange }) => {
  const { key, label, type, required, helper, options } = field;
  const displayLabel = required ? `${label} *` : label;

  if (type === "column") {
    return (
      <ColumnEditor
        label={displayLabel}
        value={value}
        page={page}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }

  if (type === "code") {
    return (
      <>
        <Typography sx={labelStyles}>{displayLabel}</Typography>
        {helper ? <Typography sx={helperStyles}>{helper}</Typography> : null}
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={6}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            mb: 2.5,
            "& textarea": {
              fontFamily: "monospace",
              fontSize: "13px",
            },
          }}
          key={key}
        />
      </>
    );
  }

  if (type === "image") {
    return (
      <ImageUploadField
        label={displayLabel}
        value={value || ""}
        page={page}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }

  if (type === "file") {
    return (
      <>
        <ImageUploadField
          label={displayLabel}
          value={value || ""}
          page={page}
          disabled={disabled}
          onChange={onChange}
          accept={field.accept || "*/*"}
          isImage={false}
          noun={field.noun || "File"}
        />
        {helper ? (
          <Typography sx={{ ...helperStyles, mt: -1, mb: 2 }}>
            {helper}
          </Typography>
        ) : null}
      </>
    );
  }

  if (type === "boolean") {
    return (
      <>
        <FormControlLabel
          control={
            <Switch
              checked={!!value}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked)}
            />
          }
          label={displayLabel}
        />
        {helper ? (
          <Typography sx={{ ...helperStyles, mb: 2 }}>{helper}</Typography>
        ) : (
          <div style={{ marginBottom: 12 }} />
        )}
      </>
    );
  }

  if (type === "select") {
    return (
      <>
        <Typography sx={labelStyles}>{displayLabel}</Typography>
        {helper ? <Typography sx={helperStyles}>{helper}</Typography> : null}
        <TextField
          select
          fullWidth
          size="small"
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mb: 2.5 }}
          key={key}
        >
          {(options || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </>
    );
  }

  if (type === "richtext") {
    return (
      <>
        <Typography sx={labelStyles}>{displayLabel}</Typography>
        {helper ? <Typography sx={helperStyles}>{helper}</Typography> : null}
        <div style={{ marginBottom: 20 }}>
          <RichTextEditor
            value={value || ""}
            disabled={disabled}
            enableFontSize
            onChange={(html) => onChange(html)}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Typography sx={labelStyles}>{displayLabel}</Typography>
      {helper ? <Typography sx={helperStyles}>{helper}</Typography> : null}
      <TextField
        fullWidth
        size="small"
        type={type === "number" ? "number" : "text"}
        multiline={type === "textarea"}
        minRows={type === "textarea" ? 3 : undefined}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) =>
          onChange(
            type === "number"
              ? e.target.value === ""
                ? ""
                : Number(e.target.value)
              : e.target.value
          )
        }
        sx={{ mb: 2.5 }}
        key={key}
      />
    </>
  );
};

export default SalesFieldRenderer;

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const helperStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#808080",
  mb: 1,
};
